import { Middleware } from '@reduxjs/toolkit';
import type { RootStore } from '../../../app/store';
import { cartActions } from '../../../reducers/cartSlice';
import { messagingActions } from '../../../reducers/messagingSlice';
import { CartItem } from '../../../utils/cart';
import { MOD_SEPARATOR } from '../../../utils/constants';
import { checkItemInCartItemTree, splitPathToItem } from '../../../utils/menu';
import { GenericMap } from '../../../utils/types';
import { draftCartActions } from '../draftCart/draftCart.slice';
import {
  selectCurrentCarSessionId,
  selectExperimentAssignment,
  selectPromptAskCounts,
  selectWithheldConstraintNames,
} from './experiments.selector';
import { promptSlotsFilled } from './experiments.slice';

// Whether the cart slot behind an ask-count key has a selection; a constraint-only key has no slot.
const isPromptSlotFilled = (
  cartItems: GenericMap<CartItem>,
  slotKey: string
) => {
  const [cartItemId, ...path] = splitPathToItem(slotKey);
  const cartItem = cartItems[cartItemId];
  if (!cartItem || !path.length) return false;
  const cartModGroup = checkItemInCartItemTree({
    cartItem,
    pathToItem: path.join(MOD_SEPARATOR),
  });
  return Object.keys(cartModGroup?.selectedItems ?? {}).length > 0;
};

// Applies a car's assignment to items already in the cart, and restarts the prompt of every slot that gets filled.
const experimentsMiddleware: Middleware =
  (store: RootStore) => (next) => (action) => {
    const before = store.getState();
    const result = next(action);
    const after = store.getState();

    if (
      action?.type === messagingActions.messageReceived.type &&
      JSON.stringify(selectExperimentAssignment(after)) !==
        JSON.stringify(selectExperimentAssignment(before))
    ) {
      const withheldConstraints = selectWithheldConstraintNames(after);
      if (withheldConstraints.length) {
        store.dispatch(
          cartActions.declineWithheldGroups({ withheldConstraints })
        );
        store.dispatch(
          draftCartActions.declineWithheldGroups({ withheldConstraints })
        );
      }
    }

    if (after.cart.cartItems !== before.cart.cartItems) {
      const filledSlotKeys = Object.keys(selectPromptAskCounts(after)).filter(
        (slotKey) => isPromptSlotFilled(after.cart.cartItems, slotKey)
      );
      if (filledSlotKeys.length) {
        store.dispatch(
          promptSlotsFilled({
            sessionId: selectCurrentCarSessionId(after),
            slotKeys: filledSlotKeys,
          })
        );
      }
    }

    return result;
  };

export default experimentsMiddleware;
